import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { getLanguageStorageKey } from '@app/i18n/i18n.service';

const htmlDirKey = 'html-dir';
const lbHtmlDirKey = 'lbHtmlDir';

/**
 * Mirrors getLanguageStorageKey — the LBL/tracking-link flow (routes under
 * /lc/) must not read or write the same layout-direction key as the main
 * customer portal.
 */
export function getHtmlDirStorageKey(): string {
  return location.pathname.startsWith('/lc/') ? lbHtmlDirKey : htmlDirKey;
}

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  language: string = 'en';

  constructor(@Inject(DOCUMENT) private document: Document) {}

  setLanguage(lang: string) {
    this.language = lang;
  }

  getLanguage() {
    const storedLanguage = localStorage.getItem(getLanguageStorageKey());
    if (storedLanguage === 'ar') {
      return storedLanguage;
    } else if (storedLanguage === 'en') {
      return 'en';
    }
    return this.language;
  }

  /**
   * Sets `dir`/`lang` on <html> from the stored (or browser) language. Runs as
   * an app initializer, before any component renders, because CDK's root
   * `Directionality` — which MatDialog, menus and other overlays use — reads
   * the document direction only once, when it is first created. Without this,
   * pages outside the shell (e.g. the /agency tracking link) and every overlay
   * stay LTR in Arabic.
   */
  applyInitialHtmlDirection(): void {
    const lang =
      localStorage.getItem(getLanguageStorageKey()) ||
      navigator.languages?.[0] ||
      navigator.language ||
      '';
    const isArabic = lang.startsWith('ar');
    const htmlTag = this.document.documentElement;
    htmlTag.dir = isArabic ? 'rtl' : 'ltr';
    htmlTag.lang = isArabic ? 'ar' : 'en';
  }

  changeHtmlDirection(lang: string) {
    let htmlTag = this.document.getElementsByTagName(
      'html',
    )[0] as HTMLHtmlElement;
    let dir = lang === 'ar' ? 'rtl' : 'ltr';
    htmlTag.dir = lang === 'ar' ? 'rtl' : 'ltr';
    localStorage.setItem(getHtmlDirStorageKey(), dir);
    return dir;
  }
}
